import React, {
    useState, useLayoutEffect, useCallback, useRef, useEffect
} from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { debounce } from 'lodash';
import Scrollparent from 'scrollparent';
import useEventListener from '../../hooks/useEventListener';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

const debounceOptions = { leading: true, trailing: true };

const SuggestionContainerWithRoot = (props) => {
    const {
        containerProps, children, height, inputRef
    } = props;
    const [dropdownParent] = useState(document.createElement('div'));
    const [xOffset, setXOffset] = useState(0);
    const [yOffset, setYOffset] = useState(0);
    const [width, setWidth] = useState(0);
    const [scrollParent, setScrollParent] = useState(null);
    const [open, setOpen] = useState(!!props.children);
    const [parentVisible, setParentVisible] = useState(true);
    const [style, setStyle] = useState({
        maxHeight: `${props.height}px`,
        minWidth: `${width}px`,
        top: `${yOffset}px`,
        left: `${xOffset}px`
    });

    useLayoutEffect(() => {
        if (containerProps.ref) {
            containerProps.ref(dropdownParent);
        }
    }, [containerProps, dropdownParent]);

    useLayoutEffect(() => {
        const styleObject = {
            maxHeight: `${height}px`,
            minWidth: `${width}px`,
            top: `${yOffset}px`,
            left: `${xOffset}px`
        };
        if (!(children && parentVisible)) {
            styleObject.display = 'none';
        }
        setStyle(styleObject);
    }, [children, height, parentVisible, yOffset, xOffset, width]);

    const updateParentVisible = useCallback((entries) => {
        if (entries[0].intersectionRatio < 1) {
            setParentVisible(false);
        } else {
            setParentVisible(true);
        }
    }, []);

    const intersectionObserver = useIntersectionObserver(scrollParent, 1, updateParentVisible);

    useLayoutEffect(() => {
        if (intersectionObserver && inputRef) {
            intersectionObserver.observe(inputRef);
        }
        return () => {
            if (intersectionObserver) {
                intersectionObserver.disconnect();
            }
        };
    }, [intersectionObserver, inputRef]);

    const updateOffsets = useCallback(() => {
        if (dropdownParent && props.inputRef && document.body.contains(dropdownParent)) {
            const currentRect = dropdownParent.getBoundingClientRect();
            const targetRect = props.inputRef.getBoundingClientRect();

            const xOffset = targetRect.left - currentRect.left;
            // the target div is 1px high and is BEHIND the bottom of the texBox
            // so the offset needs to be 2 pixels lower to start with a one pixel gap
            // between the dropdown and the textbox
            const yOffset = targetRect.top - currentRect.top + 2;
            const { width } = targetRect;

            setXOffset(xOffset);
            setYOffset(yOffset);
            setWidth(width);
        }
    }, [dropdownParent, props.inputRef]);

    useEventListener(
        'scroll',
        debounce(updateOffsets, 100, debounceOptions),
        scrollParent
    );
    useEventListener('resize', updateOffsets, window);

    useLayoutEffect(() => {
        updateOffsets();
    }, [open, updateOffsets]);

    useLayoutEffect(() => {
        setOpen(!!props.children);
    }, [props.children]);

    useLayoutEffect(() => {
        dropdownParent.setAttribute('class', 'react-autosuggest__container');
        document.body.appendChild(dropdownParent);
        updateOffsets();
        return () => {
            document.body.removeChild(dropdownParent);
        };
    }, [dropdownParent, updateOffsets]);

    useEffect(() => {
        setScrollParent(Scrollparent(inputRef));
    }, [inputRef]);

    return ReactDOM.createPortal((
        <div
            { ...props.containerProps }
            className={ props.styles.dropdown }
            style={ style }
        >
            { props.children }
        </div>
    ), dropdownParent);
};

const SuggestionContainerWithoutRoot = (props) => {
    const {
        height,
        children,
        containerProps,
        styles
    } = props;
    const ref = useRef(null);
    const [style, setStyle] = useState({
        maxHeight: `${props.height}px`
    });
    useLayoutEffect(() => {
        if (ref.current && props.containerProps.ref) {
            props.containerProps.ref(ref.current);
        }
    });

    useLayoutEffect(() => {
        const styleObject = {
            maxHeight: `${height}px`
        };
        if (!children) {
            styleObject.display = 'none';
        }
        setStyle(styleObject);
    }, [children, height]);

    return (
        <div
            { ...containerProps }
            ref={ ref }
            className={ styles.dropdown }
            style={ style }
        >
            { children }
        </div>
    );
};

export const SuggestionsContainer = (props) => (
    props.portal ? <SuggestionContainerWithRoot { ...props } /> : <SuggestionContainerWithoutRoot { ...props } />
);

SuggestionsContainer.propTypes = {
    containerProps: PropTypes.shape({
        ref: PropTypes.func
    }).isRequired,
    children: PropTypes.shape({}),
    styles: PropTypes.shape({
        dropdown: PropTypes.string
    }).isRequired,
    height: PropTypes.number.isRequired,
    portal: PropTypes.bool
};

SuggestionContainerWithoutRoot.propTypes = {
    containerProps: PropTypes.shape({
        ref: PropTypes.func
    }).isRequired,
    children: PropTypes.shape({}),
    styles: PropTypes.shape({
        dropdown: PropTypes.string
    }).isRequired,
    height: PropTypes.number.isRequired
};

SuggestionsContainer.defaultProps = {
    children: null,
    portal: false
};

SuggestionContainerWithoutRoot.defaultProps = {
    children: null
};

export const makeRenderSuggestion = (getDisplayValue, styles) => (item) => (
    <div className={ styles.suggestion }>
        { getDisplayValue(item) }
    </div>
);
